import type { PartnerContent } from './partner.types';

export const PARTNER_EN: PartnerContent = {
  hero: {
    title: 'HotelStack.io Partner Program',
    subtitle: 'Earn recurring commissions by referring hotels to the leading cloud-based property management platform. Join our growing network of hospitality partners.',
    applyBtn: 'Apply Now',
    exploreBtn: 'Explore Platform',
  },
  benefits: {
    title: 'Why Partner with HotelStack.io',
    subtitle: 'Industry-leading commissions, enterprise tools, and dedicated support',
    items: [
      { title: 'Recurring Commissions', description: 'Earn 15% commission on subscription revenue from every hotel you refer for the first 12 months, then 10% ongoing.' },
      { title: 'Dedicated Partner Support', description: 'Priority support channel with a dedicated partner success manager to help you close deals and onboard hotels.' },
      { title: 'Sales & Marketing Resources', description: 'Co-branded marketing materials, demo environments, pitch decks, and case studies to accelerate your outreach.' },
      { title: 'Partner Portal Access', description: 'Track referrals, commissions, and performance analytics in real time through your personalized partner dashboard.' },
      { title: 'Enterprise-Grade Platform', description: 'Sell with confidence: SOC 2 certified, 99.9% uptime SLA, AES-256 encryption, and 24/7 monitoring.' },
      { title: 'Multi-Property Scale', description: 'Earn on every property in a chain. The more hotels you onboard, the higher your commission tier climbs.' },
    ],
  },
  tiers: {
    title: 'Commission Tiers',
    subtitle: 'The more hotels you bring, the more you earn',
    items: [
      { name: 'Silver Partner', commission: '10%', commLabel: 'recurring commission', requirements: ['1-4 referred hotels', 'Basic co-branded materials', 'Standard support channel', 'Monthly commission payouts'], highlight: false },
      { name: 'Gold Partner', commission: '15%', badge: 'Most Popular', commLabel: 'recurring commission', requirements: ['5-14 referred hotels', 'Premium marketing kit', 'Dedicated partner manager', 'Bi-weekly commission payouts', 'Priority onboarding for referrals'], highlight: true },
      { name: 'Platinum Partner', commission: '20%', commLabel: 'recurring commission', requirements: ['15+ referred hotels', 'Custom co-branded portal', 'Executive partner liaison', 'Weekly commission payouts', 'Revenue share on upsells', 'Exclusive territory rights'], highlight: false },
    ],
  },
  howItWorks: {
    title: 'How It Works',
    subtitle: 'Four simple steps to start earning',
    steps: [
      { title: 'Apply to Join', description: 'Fill out the partner application form. Our team will review your profile and reach out within 2 business days.' },
      { title: 'Get Onboarded', description: 'Attend a partner kickoff session. Receive your demo environment, sales materials, and unique referral tracking link.' },
      { title: 'Refer Hotels', description: 'Introduce HotelStack.io to hotels in your network. We handle product demos, onboarding, and technical setup.' },
      { title: 'Earn Commissions', description: 'Earn recurring commissions on every active subscription. Track everything in real time from your partner dashboard.' },
    ],
  },
  whoCanJoin: {
    title: 'Who Can Join',
    subtitle: 'Our partner program is open to hospitality professionals and organizations',
    items: ['Hotel management companies', 'Travel agencies and tour operators', 'Hospitality consultants and advisors', 'Technology integrators and resellers', 'Franchise operators and brand owners', 'Industry associations and networks'],
  },
  cta: {
    title: 'Ready to Grow with HotelStack.io?',
    subtitle: 'Join our partner network and start earning recurring commissions today. No upfront investment required.',
    applyBtn: 'Apply to Partner Program',
    contactBtn: 'Contact Sales',
    note: 'Applications are reviewed within 2 business days',
  },
};

export const PARTNER_AR: PartnerContent = {
  hero: {
    title: 'برنامج شركاء HotelStack.io',
    subtitle: 'اكسب عمولات متكررة من خلال إحالة الفنادق إلى منصة إدارة الممتلكات السحابية الرائدة. انضم إلى شبكتنا المتنامية من شركاء الضيافة.',
    applyBtn: 'تقدم الآن',
    exploreBtn: 'استكشف المنصة',
  },
  benefits: {
    title: 'لماذا تشارك مع HotelStack.io',
    subtitle: 'عمولات رائدة في الصناعة وأدوات مؤسسية ودعم مخصص',
    items: [
      { title: 'عمولات متكررة', description: 'اكسب ١٥% عمولة على إيرادات الاشتراك من كل فندق تحيله خلال أول ١٢ شهراً، ثم ١٠% مستمرة.' },
      { title: 'دعم شركاء مخصص', description: 'قناة دعم ذات أولوية مع مدير نجاح شريك مخصص لمساعدتك في إتمام الصفقات.' },
      { title: 'موارد المبيعات والتسويق', description: 'مواد تسويقية مشتركة وبيئات عرض وعروض تقديمية ودراسات حالة لتسريع انتشارك.' },
      { title: 'الوصول إلى بوابة الشركاء', description: 'تتبع الإحالات والعمولات وتحليلات الأداء في الوقت الفعلي.' },
      { title: 'منصة بمستوى المؤسسات', description: 'بِع بثقة: شهادة أمنية، اتفاقية وقت تشغيل ٩٩٫٩%، تشفير متقدم، ومراقبة على مدار الساعة.' },
      { title: 'نطاق متعدد الممتلكات', description: 'اكسب من كل ممتلكات في سلسلة. كلما زاد عدد الفنادق، ارتفع مستوى عمولتك.' },
    ],
  },
  tiers: {
    title: 'مستويات العمولة',
    subtitle: 'كلما أحلت المزيد من الفنادق، كلما كسبت أكثر',
    items: [
      { name: 'شريك فضي', commission: '١٠%', commLabel: 'عمولة متكررة', requirements: ['١-٤ فنادق محالة', 'مواد أساسية مشتركة', 'قناة دعم قياسية', 'دفعات عمولة شهرية'], highlight: false },
      { name: 'شريك ذهبي', commission: '١٥%', badge: 'الأكثر شعبية', commLabel: 'عمولة متكررة', requirements: ['٥-١٤ فندقاً محالاً', 'حزمة تسويق متميزة', 'مدير شريك مخصص', 'دفعات كل أسبوعين', 'أولوية في الإعداد'], highlight: true },
      { name: 'شريك بلاتيني', commission: '٢٠%', commLabel: 'عمولة متكررة', requirements: ['+١٥ فندقاً محالاً', 'بوابة مخصصة مشتركة', 'مسؤول تنفيذي للشركاء', 'دفعات أسبوعية', 'مشاركة في إيرادات الترقيات', 'حقوق حصرية للمنطقة'], highlight: false },
    ],
  },
  howItWorks: {
    title: 'كيف يعمل',
    subtitle: 'أربع خطوات بسيطة للبدء في الكسب',
    steps: [
      { title: 'تقدم للانضمام', description: 'املأ نموذج طلب الشراكة. سيراجع فريقنا ملفك ويتواصل معك خلال يومي عمل.' },
      { title: 'احصل على التدريب', description: 'احضر جلسة انطلاق الشريك. احصل على بيئة عرض ومواد مبيعات ورابط تتبع إحالة فريد.' },
      { title: 'أحل الفنادق', description: 'قدم HotelStack.io للفنادق في شبكتك. نحن نتولى عروض المنتج والإعداد والإعداد التقني.' },
      { title: 'اكسب العمولات', description: 'اكسب عمولات متكررة على كل اشتراك نشط. تتبع كل شيء في الوقت الفعلي.' },
    ],
  },
  whoCanJoin: {
    title: 'من يمكنه الانضمام',
    subtitle: 'برنامج الشراكة مفتوح للمهنيين والمنظمات في مجال الضيافة',
    items: ['شركات إدارة الفنادق', 'وكالات السفر ومنظمو الرحلات', 'مستشارو ومستشارو الضيافة', 'مكاملو التكنولوجيا والموزعون', 'مشغلو الامتيازات وأصحاب العلامات التجارية', 'جمعيات وشبكات الصناعة'],
  },
  cta: {
    title: 'مستعد للنمو مع HotelStack.io؟',
    subtitle: 'انضم إلى شبكة شركائنا وابدأ في كسب عمولات متكررة اليوم. لا يلزم استثمار مسبق.',
    applyBtn: 'تقدم لبرنامج الشراكة',
    contactBtn: 'اتصل بالمبيعات',
    note: 'تتم مراجعة الطلبات خلال يومي عمل',
  },
};

export const PARTNER_TH: PartnerContent = {
  hero: {
    title: 'โปรแกรมพันธมิตร HotelStack.io',
    subtitle: 'รับค่าคอมมิชชันต่อเนื่องจากการแนะนำโรงแรมสู่แพลตฟอร์มจัดการทรัพย์สินบนคลาวด์ชั้นนำ เข้าร่วมเครือข่ายพันธมิตรด้านการบริการที่เติบโตของเรา',
    applyBtn: 'สมัครเลย',
    exploreBtn: 'สำรวจแพลตฟอร์ม',
  },
  benefits: {
    title: 'ทำไมต้องเป็นพันธมิตรกับ HotelStack.io',
    subtitle: 'ค่าคอมมิชชันชั้นนำในอุตสาหกรรม เครื่องมือระดับองค์กร และการสนับสนุนเฉพาะ',
    items: [
      { title: 'ค่าคอมมิชชันต่อเนื่อง', description: 'รับค่าคอมมิชชัน 15% จากรายได้สมาชิกของทุกโรงแรมที่คุณแนะนำใน 12 เดือนแรก จากนั้น 10% ต่อเนื่อง' },
      { title: 'การสนับสนุนพันธมิตรเฉพาะ', description: 'ช่องทางสนับสนุนลำดับความสำคัญพร้อมผู้จัดการความสำเร็จพันธมิตรเฉพาะ' },
      { title: 'ทรัพยากรการขายและการตลาด', description: 'สื่อการตลาดร่วมแบรนด์ สภาพแวดล้อมสาธิต สไลด์นำเสนอ และกรณีศึกษา' },
      { title: 'การเข้าถึงพอร์ทัลพันธมิตร', description: 'ติดตามการแนะนำ ค่าคอมมิชชัน และการวิเคราะห์ประสิทธิภาพแบบเรียลไทม์' },
      { title: 'แพลตฟอร์มระดับองค์กร', description: 'ขายอย่างมั่นใจ: ได้รับการรับรอง SOC 2, SLA เวลาทำงาน 99.9%, การเข้ารหัส AES-256' },
      { title: 'ขยายหลายทรัพย์สิน', description: 'รับรายได้จากทุกทรัพย์สินในเครือ ยิ่งคุณเพิ่มโรงแรมมากขึ้น ระดับค่าคอมมิชชันยิ่งสูงขึ้น' },
    ],
  },
  tiers: {
    title: 'ระดับค่าคอมมิชชัน',
    subtitle: 'ยิ่งนำโรงแรมมามากขึ้น ยิ่งได้รับมากขึ้น',
    items: [
      { name: 'พันธมิตรเงิน', commission: '10%', commLabel: 'ค่าคอมมิชชันต่อเนื่อง', requirements: ['1-4 โรงแรมที่แนะนำ', 'สื่อร่วมแบรนด์พื้นฐาน', 'ช่องทางสนับสนุนมาตรฐาน', 'จ่ายค่าคอมมิชชันรายเดือน'], highlight: false },
      { name: 'พันธมิตรทอง', commission: '15%', badge: 'ยอดนิยม', commLabel: 'ค่าคอมมิชชันต่อเนื่อง', requirements: ['5-14 โรงแรมที่แนะนำ', 'ชุดการตลาดพรีเมียม', 'ผู้จัดการพันธมิตรเฉพาะ', 'จ่ายทุกสองสัปดาห์', 'การเริ่มต้นใช้งานลำดับความสำคัญ'], highlight: true },
      { name: 'พันธมิตรแพลตตินัม', commission: '20%', commLabel: 'ค่าคอมมิชชันต่อเนื่อง', requirements: ['15+ โรงแรมที่แนะนำ', 'พอร์ทัลร่วมแบรนด์ที่กำหนดเอง', 'ผู้ประสานงานระดับผู้บริหาร', 'จ่ายรายสัปดาห์', 'แบ่งปันรายได้จากการอัปเกรด', 'สิทธิ์พื้นที่เฉพาะ'], highlight: false },
    ],
  },
  howItWorks: {
    title: 'วิธีการทำงาน',
    subtitle: 'สี่ขั้นตอนง่ายๆ เพื่อเริ่มต้นรับรายได้',
    steps: [
      { title: 'สมัครเข้าร่วม', description: 'กรอกแบบฟอร์มสมัครพันธมิตร ทีมงานจะตรวจสอบโปรไฟล์และติดต่อกลับภายใน 2 วันทำการ' },
      { title: 'รับการฝึกอบรม', description: 'เข้าร่วมเซสชันเริ่มต้นพันธมิตร รับสภาพแวดล้อมสาธิต สื่อการขาย และลิงก์ติดตามการแนะนำ' },
      { title: 'แนะนำโรงแรม', description: 'แนะนำ HotelStack.io ให้กับโรงแรมในเครือข่าย เราจัดการสาธิตผลิตภัณฑ์ การเริ่มต้นใช้งาน และการตั้งค่าทางเทคนิค' },
      { title: 'รับค่าคอมมิชชัน', description: 'รับค่าคอมมิชชันต่อเนื่องจากทุกสมาชิกที่ใช้งาน ติดตามทุกอย่างแบบเรียลไทม์' },
    ],
  },
  whoCanJoin: {
    title: 'ใครสามารถเข้าร่วมได้',
    subtitle: 'โปรแกรมพันธมิตรเปิดรับผู้เชี่ยวชาญและองค์กรด้านการบริการ',
    items: ['บริษัทจัดการโรงแรม', 'ตัวแทนท่องเที่ยวและผู้ดำเนินการทัวร์', 'ที่ปรึกษาด้านการบริการ', 'ผู้รวมเทคโนโลยีและตัวแทนจำหน่าย', 'ผู้ดำเนินการแฟรนไชส์และเจ้าของแบรนด์', 'สมาคมและเครือข่ายอุตสาหกรรม'],
  },
  cta: {
    title: 'พร้อมเติบโตกับ HotelStack.io?',
    subtitle: 'เข้าร่วมเครือข่ายพันธมิตรและเริ่มรับค่าคอมมิชชันต่อเนื่องวันนี้ ไม่ต้องลงทุนล่วงหน้า',
    applyBtn: 'สมัครโปรแกรมพันธมิตร',
    contactBtn: 'ติดต่อฝ่ายขาย',
    note: 'คำร้องจะได้รับการตรวจสอบภายใน 2 วันทำการ',
  },
};
